/**
 * Hook: 获取 System 下所有 k8s-environment 类型的 Resource 实体
 */

import { useState, useEffect } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import {
  RELATION_HAS_PART,
  Entity,
} from '@backstage/catalog-model';
import { EnvironmentInfo, K8S_ENVIRONMENT_ANNOTATIONS } from '../types';

interface UseEnvironmentsResult {
  environments: EnvironmentInfo[];
  loading: boolean;
  error: Error | null;
}

/**
 * 从 Resource 实体解析为 EnvironmentInfo
 */
function parseEnvironment(entity: Entity): EnvironmentInfo | null {
  const annotations = entity.metadata.annotations || {};
  const cluster = annotations[K8S_ENVIRONMENT_ANNOTATIONS.K8S_CLUSTER];
  const namespace = annotations[K8S_ENVIRONMENT_ANNOTATIONS.K8S_NAMESPACE];

  if (!cluster || !namespace) {
    return null;
  }

  return {
    name: entity.metadata.name,
    label: annotations[K8S_ENVIRONMENT_ANNOTATIONS.ENVIRONMENT_NAME] || entity.metadata.name,
    type: annotations[K8S_ENVIRONMENT_ANNOTATIONS.ENVIRONMENT_TYPE] || 'unknown',
    cluster,
    namespace,
    imageRegistry: annotations[K8S_ENVIRONMENT_ANNOTATIONS.IMAGE_REGISTRY],
    tektonCluster: annotations[K8S_ENVIRONMENT_ANNOTATIONS.TEKTON_CLUSTER],
    tektonNamespace: annotations[K8S_ENVIRONMENT_ANNOTATIONS.TEKTON_NAMESPACE],
    entityJson: JSON.stringify(entity),
  };
}

/**
 * 获取当前 System 下所有 k8s-environment Resource 实体
 *
 * 实现原理：
 * 1. 从当前 System Entity 的 relations 中找到 hasPart 关系的 Resource
 * 2. 获取这些 Resource 实体的详情
 * 3. 过滤出 spec.type === 'k8s-environment' 的 Resource
 * 4. 从注解中解析出 EnvironmentInfo
 */
export function useEnvironments(): UseEnvironmentsResult {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const [environments, setEnvironments] = useState<EnvironmentInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchEnvironments() {
      setLoading(true);
      setError(null);

      try {
        // 获取 System 下所有关联的 Resource
        // 1. 从 relations 中找 hasPart / dependsOn 关系的 Resource
        const relations = entity.relations || [];
        const resourceRefsFromRelations = relations
          .filter(r =>
            (r.type === RELATION_HAS_PART || r.type === 'dependsOn') &&
            r.targetRef.startsWith('resource:')
          )
          .map(r => r.targetRef);

        // 2. 从 System 注解中找 hose.io/k8s-resource-* 引用
        const systemAnnotations = entity.metadata.annotations || {};
        const resourceRefsFromAnnotations: string[] = [];
        for (const [key, value] of Object.entries(systemAnnotations)) {
          if (key.startsWith('hose.io/k8s-resource-') && value) {
            // value 格式: resource:default/xxx
            resourceRefsFromAnnotations.push(value);
          }
        }

        // 合并去重
        const resourceRefs = [...new Set([...resourceRefsFromRelations, ...resourceRefsFromAnnotations])];

        if (resourceRefs.length === 0) {
          setEnvironments([]);
          setLoading(false);
          return;
        }

        // 批量获取 Resource 实体
        const resourceEntities = await Promise.all(
          resourceRefs.map(async ref => {
            try {
              return await catalogApi.getEntityByRef(ref);
            } catch {
              return undefined;
            }
          }),
        );

        // 过滤出 k8s-environment 类型的 Resource
        const envEntities = resourceEntities.filter(
          (e): e is Entity =>
            e !== undefined &&
            (e as any).spec?.type === 'k8s-environment',
        );

        // 解析为 EnvironmentInfo
        const envInfos = envEntities
          .map(parseEnvironment)
          .filter((e): e is EnvironmentInfo => e !== null);

        if (!cancelled) {
          setEnvironments(envInfos);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchEnvironments();
    return () => { cancelled = true; };
  }, [entity, catalogApi]);

  return { environments, loading, error };
}
